import api from 'services/api';
import { array, bool, enforce, nullable, num, object, runSchema, str } from 'validation';

// GET /node_instances/:node_instance_id/wait_events (routes/node_instances.js). The endpoint returns
// the two wait-event sources as separate arrays, each in its native shape and enriched with the
// resolved event identity + `raised`:
//   - node_wait_events: the node_instance_events snapshot (regular nodes).
//   - flow_wait_events: flow_instance_nodes.wait_events JSONB entries (events declared on a flow node),
//     referenced by a 'Zone|EVENT' path.
// This endpoint validates both wire arrays; the getNodeInstanceWaitEvents operation flattens them into
// one canonical, source-tagged WaitEvent list.
//
// `raised` means "a raised event_instances row exists for this event" — the same predicate the
// scheduler uses to satisfy a wait. It deliberately ignores `precondition`: a wait whose precondition
// is false (incl. a skipped wait) is treated as already passed even with no raised instance, so
// `raised` can read false while the node is not actually blocked on it. `precondition` is surfaced
// verbatim so the operator can interpret a grey badge.
//
// Flow `event_id`/`event_type` are null when a path does not resolve to a known event; such rows still
// render by name but without an instances link.

export interface NodeWaitEventWire {
  readonly node_instance_events_id: number;
  readonly event_id: number;
  readonly event_name: string;
  readonly event_type: string;
  readonly zone_id: number;
  readonly zone_name: string;
  readonly auto_submit: boolean;
  readonly auto_clear: boolean;
  readonly scheduling_window_id: number | null;
  readonly precondition: string | null;
  readonly raised: boolean;
}

export interface FlowWaitEventWire {
  readonly path: string;
  readonly event_id: number | null;
  readonly event_name: string;
  readonly event_type: string | null;
  readonly zone_name: string;
  readonly auto_submit: boolean;
  readonly auto_clear: boolean;
  readonly scheduling_window: string | null;
  readonly precondition: string | null;
  readonly raised: boolean;
}

const nodeWaitEventWireSchema = object<NodeWaitEventWire>({
  node_instance_events_id: num(),
  event_id: num(),
  event_name: str(),
  event_type: str(),
  zone_id: num(),
  zone_name: str(),
  auto_submit: bool(),
  auto_clear: bool(),
  scheduling_window_id: nullable(num()),
  precondition: nullable(str()),
  raised: bool(),
});

const flowWaitEventWireSchema = object<FlowWaitEventWire>({
  path: str(),
  event_id: nullable(num()),
  event_name: str(),
  event_type: nullable(str()),
  zone_name: str(),
  auto_submit: bool(),
  auto_clear: bool(),
  scheduling_window: nullable(str()),
  precondition: nullable(str()),
  raised: bool(),
});

export interface WaitEventsResponseWire {
  readonly node_wait_events: readonly NodeWaitEventWire[];
  readonly flow_wait_events: readonly FlowWaitEventWire[];
}

const waitEventsResponseSchema = object<WaitEventsResponseWire>({
  node_wait_events: array(nodeWaitEventWireSchema),
  flow_wait_events: array(flowWaitEventWireSchema),
});

/**
 * Fetches the raw wait-event wire for a node instance (the two source arrays). Throws only if the
 * response envelope is structurally malformed; rows whose identity drifts are dropped individually.
 */
export const getNodeInstanceWaitEventsWire = async (nodeInstanceId: number | string): Promise<WaitEventsResponseWire> => {
  const res = await api.get(`/node_instances/${encodeURIComponent(String(nodeInstanceId))}/wait_events`);
  const parsed = runSchema(waitEventsResponseSchema, res.data, 'getNodeInstanceWaitEventsWire');
  if (!parsed.ok) {
    throw new Error('getNodeInstanceWaitEventsWire: response failed validation');
  }
  return enforce(parsed.value, parsed.warnings, ['node_wait_events[*].node_instance_events_id', 'flow_wait_events[*].path']);
};
